import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import api from '../services/api';
import config from '../config';
import DemoModal from '../components/common/DemoModal';
import WhatsAppButton from '../components/common/WhatsAppButton';
import {
  HOMEPAGE_DEFAULTS,
  deepMergeHomepage,
  toEmbedUrl,
  isDirectVideoFile,
  getDemoVideoUrl
} from '../constants/homepageDefaults';
import '../styles/landing.css';

const Landing = () => {
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isDemoModalOpen, setIsDemoModalOpen] = useState(false);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [isAnnualPricing, setIsAnnualPricing] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeFaq, setActiveFaq] = useState(null);
  const [content, setContent] = useState(deepMergeHomepage(HOMEPAGE_DEFAULTS));
  const [logoUrl, setLogoUrl] = useState(null);

  // Handle scroll for sticky nav shadow
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Fetch admin content
  useEffect(() => {
    let cancelled = false;
    
    const fetchData = async () => {
      try {
        const [siteRes, plansRes] = await Promise.all([
          api.get('/site-content.php').catch(() => ({ data: { content: {} } })),
          api.get('/public-plans.php').catch(() => ({ data: { plans: [] } }))
        ]);
        
        if (!cancelled) {
          const merged = deepMergeHomepage(siteRes.data?.content || {});
          
          if (plansRes.data?.plans?.length > 0) {
            merged.pricing = merged.pricing || {};
            merged.pricing.plans = plansRes.data.plans.map(p => ({
              name: p.plan_name,
              desc: `Up to ${p.max_users} users, ${p.max_products} products`,
              priceMonthly: p.price_monthly,
              priceAnnually: p.price_yearly || (p.price_monthly * 12),
              cta: 'Start Free Trial',
              popular: false,
              features: typeof p.features === 'object' && p.features !== null 
                ? Object.entries(p.features)
                    .filter(([k, v]) => v)
                    .map(([k, v]) => v === true ? k.charAt(0).toUpperCase() + k.slice(1) : `${k.charAt(0).toUpperCase() + k.slice(1)}: ${v}`)
                : (Array.isArray(p.features) ? p.features : [])
            }));
          }
          
          setContent(merged);
          if (merged.brand?.logoUrl) {
            const path = merged.brand.logoUrl;
            if (path.startsWith('http')) {
              setLogoUrl(path);
            } else {
              setLogoUrl(path.startsWith('/') ? path : `/${path}`);
            }
          }
        }
      } catch (err) {
        console.warn('Failed to load site content', err);
      }
    };
    
    fetchData();
    return () => { cancelled = true; };
  }, []);

  const toggleFaq = (index) => {
    setActiveFaq(activeFaq === index ? null : index);
  };

  const closeMobileMenu = () => setIsMobileMenuOpen(false);

  const { brand, hero, trust, problems, demo, capabilities, industries, howItWorks, localization, caseStudy, pricing, faq, cta, footer } = content;

  const demoVideoUrl = getDemoVideoUrl(demo);
  const demoEmbedUrl = toEmbedUrl(demoVideoUrl);

  const scrollToDemo = () => {
    const el = document.getElementById('demo');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const playDemoVideo = () => {
    setIsVideoPlaying(true);
    scrollToDemo();
  };

  // Secondary CTA: external URL, app route, #demo (play video), or the booking form
  const handleSecondaryCta = (link) => {
    closeMobileMenu();
    const target = (link || '').trim();

    if (/^https?:\/\//i.test(target)) {
      window.open(target, '_blank', 'noopener,noreferrer');
      return;
    }
    if (target === '#demo') {
      if (demo?.enabled && demoEmbedUrl) {
        playDemoVideo();
      } else {
        setIsDemoModalOpen(true);
      }
      return;
    }
    if (target.startsWith('#') && target.length > 1 && document.getElementById(target.slice(1))) {
      document.getElementById(target.slice(1)).scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    if (target.startsWith('/')) {
      navigate(target);
      return;
    }
    setIsDemoModalOpen(true);
  };

  return (
    <>
      <Helmet>
        <title>{`${brand.productName} | ${brand.tagline}`}</title>
        <meta name="description" content={hero.subheadline} />
        <meta property="og:title" content={`${brand.productName} | ${brand.tagline}`} />
        <meta property="og:description" content={hero.subheadline} />
        <meta property="og:type" content="website" />
      </Helmet>

      <div className="landing-page">
        {/* NAVIGATION */}
        <nav className={`navbar ${scrolled ? 'scrolled' : ''}`}>
          <div className="container nav-container">
            <Link to="/" className="brand-logo" onClick={closeMobileMenu}>
              {logoUrl ? (
                <>
                  <img src={logoUrl} alt={brand.logoAlt || 'Logo'} className="site-logo-img" style={{ height: '40px', objectFit: 'contain' }} />
                  <span className="brand-text">{brand.productName}</span>
                </>
              ) : (
                <>
                  <span className="brand-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/></svg>
                  </span>
                  <span className="brand-text">{brand.productName}</span>
                </>
              )}
            </Link>

            <button className="mobile-toggle" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle menu">
              {isMobileMenuOpen ? (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
              ) : (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 12h18M3 6h18M3 18h18"/></svg>
              )}
            </button>

            <div className={`nav-menu ${isMobileMenuOpen ? 'open' : ''}`}>
              <div className="nav-links">
                <a href="#features" onClick={closeMobileMenu}>Features</a>
                <a href="#industries" onClick={closeMobileMenu}>Industries</a>
                <a href="#pricing" onClick={closeMobileMenu}>Pricing</a>
                <a href="#faq" onClick={closeMobileMenu}>FAQ</a>
              </div>
              <div className="nav-actions">
                <Link to="/login" className="btn-text" onClick={closeMobileMenu}>Sign In</Link>
                <button className="btn-ghost" onClick={() => handleSecondaryCta(hero.secondaryCtaLink)}>{hero.secondaryCta}</button>
                <Link to={hero.primaryCtaLink} className="btn-primary" onClick={closeMobileMenu}>{hero.primaryCta}</Link>
              </div>
            </div>
          </div>
        </nav>

        {/* HERO SECTION */}
        <section className="hero">
          <div className="container hero-container">
            <div className="hero-content">
              {hero.badge && <div className="hero-badge">{hero.badge}</div>}
              <h1 className="hero-headline">{hero.headline}</h1>
              <p className="hero-subheadline">{hero.subheadline}</p>
              <div className="hero-cta-group">
                <Link to={hero.primaryCtaLink} className="btn-primary btn-large">{hero.primaryCta}</Link>
                {hero.secondaryCta && (
                  <button onClick={() => handleSecondaryCta(hero.secondaryCtaLink)} className="btn-secondary btn-large">{hero.secondaryCta}</button>
                )}
              </div>
              {hero.microcopy && (
                <p className="hero-microcopy">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 6L9 17l-5-5"/></svg>
                  {' '}{hero.microcopy}
                </p>
              )}
            </div>
            <div className="hero-visual">
              {hero.imageUrl && hero.imageUrl !== '/images/pos-preview.png' ? (
                <img src={hero.imageUrl} alt="Hero" className="hero-image" style={{ width: '100%', height: 'auto', borderRadius: '12px', boxShadow: '0 20px 40px rgba(0,0,0,0.1)' }} />
              ) : (
                <div className="browser-mockup">
                  <div className="browser-header">
                    <span className="dot red"></span>
                    <span className="dot yellow"></span>
                    <span className="dot green"></span>
                  </div>
                  <div className="browser-body">
                    <div className="dashboard-placeholder">
                      <div className="dash-sidebar"></div>
                      <div className="dash-main">
                        <div className="dash-header"></div>
                        <div className="dash-stats">
                          <div className="stat-box"></div>
                          <div className="stat-box"></div>
                          <div className="stat-box"></div>
                        </div>
                        <div className="dash-chart"></div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* TRUST SECTION */}
        {trust?.logos?.length > 0 && (
          <section className="trust">
            <div className="container">
              <p className="trust-headline">{trust.headline}</p>
              <div className="trust-logos">
                {trust.logos.map((logo, i) => (
                  <div key={i} className="trust-logo-placeholder">{logo}</div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* BUSINESS PROBLEMS SECTION */}
        {problems?.items?.length > 0 && (
          <section className="problems bg-light">
            <div className="container">
              <h2 className="section-title">{problems.sectionTitle}</h2>
              <div className="problems-grid">
                {problems.items.map((item, i) => (
                  <div key={i} className="problem-card">
                    <svg className="problem-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0zM12 9v4M12 17h.01"/></svg>
                    <p>{item.text}</p>
                  </div>
                ))}
              </div>
              <div className="solution-transition">
                <h3>{problems.solutionTransitionTitle}</h3>
                <div className="workflow-visual">
                  <div className="workflow-step">SALE</div>
                  <div className="workflow-arrow">↓</div>
                  <div className="workflow-step">STOCK UPDATES</div>
                  <div className="workflow-arrow">↓</div>
                  <div className="workflow-step">CUSTOMER RECORD UPDATES</div>
                  <div className="workflow-arrow">↓</div>
                  <div className="workflow-step">REPORTS UPDATE</div>
                  <div className="workflow-arrow">↓</div>
                  <div className="workflow-step highlight">OWNER SEES BUSINESS PERFORMANCE</div>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* DEMONSTRATION SECTION */}
        {demo?.enabled && (
          <section id="demo" className="demo-section">
            <div className="container">
              <div className="demo-header">
                <h2 className="section-title">{demo.sectionTitle}</h2>
                <p className="section-subtitle">{demo.sectionSubtitle}</p>
              </div>
              <div className="video-container">
                {demoEmbedUrl && isVideoPlaying ? (
                  isDirectVideoFile(demoEmbedUrl) ? (
                    <video className="demo-video" src={demoEmbedUrl} controls autoPlay playsInline />
                  ) : (
                    <iframe
                      className="demo-video"
                      src={`${demoEmbedUrl}${demoEmbedUrl.includes('?') ? '&' : '?'}autoplay=1&rel=0`}
                      title={`${brand.productName} demo video`}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  )
                ) : (
                  <div className="video-placeholder">
                    <button
                      className="play-button"
                      onClick={() => (demoEmbedUrl ? setIsVideoPlaying(true) : setIsDemoModalOpen(true))}
                      aria-label={demoEmbedUrl ? 'Play demo video' : 'Book a demo'}
                    >
                      <svg viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
                    </button>
                    <p>{demoEmbedUrl ? `Watch ${brand.productName} in action` : `Book a live ${brand.productName} demo`}</p>
                  </div>
                )}
              </div>
            </div>
          </section>
        )}

        {/* CAPABILITIES SECTION */}
        {capabilities?.items?.length > 0 && (
          <section id="features" className="capabilities bg-light">
            <div className="container">
              <h2 className="section-title">{capabilities.sectionTitle}</h2>
              <div className="capabilities-grid">
                {capabilities.items.map((item, i) => (
                  <div key={i} className="capability-card">
                    <div className="cap-icon">{item.icon}</div>
                    <h3>{item.title}</h3>
                    <p>{item.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* INDUSTRIES SECTION */}
        {industries?.items?.length > 0 && (
          <section id="industries" className="industries">
            <div className="container">
              <h2 className="section-title">{industries.sectionTitle}</h2>
              <div className="industry-grid">
                {industries.items.map((item, i) => (
                  <Link key={i} to={item.link || '#'} className={`industry-card ${item.highlight ? 'highlight-card' : ''}`}>
                    <div className="ind-icon">{item.icon}</div>
                    <h3>{item.title}</h3>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* HOW IT WORKS SECTION */}
        {howItWorks?.steps?.length > 0 && (
          <section className="how-it-works bg-light">
            <div className="container">
              <h2 className="section-title">{howItWorks.sectionTitle}</h2>
              <div className="steps-container">
                {howItWorks.steps.map((step, i) => (
                  <div key={i} className="step">
                    <div className="step-number">{step.number}</div>
                    <h3>{step.title}</h3>
                    <p>{step.description}</p>
                  </div>
                ))}
              </div>
              <div className="text-center mt-4">
                <Link to="/register" className="btn-primary btn-large">Start Free</Link>
              </div>
            </div>
          </section>
        )}

        {/* LOCALIZATION SECTION */}
        {localization?.features?.length > 0 && (
          <section className="localization">
            <div className="container">
              <h2 className="section-title text-center">{localization.sectionTitle}</h2>
              <div className="local-features">
                {localization.features.map((feature, i) => (
                  <div key={i} className="local-feature">
                    <div className="check-icon">✓</div>
                    <span>{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* CASE STUDY */}
        {caseStudy?.quote && (
          <section className="case-study bg-dark text-white">
            <div className="container">
              <div className="case-study-content">
                <div className="case-quote">{caseStudy.quote}</div>
                <div className="case-author">
                  <strong>{caseStudy.authorName}</strong>
                  <span>{caseStudy.authorRole}</span>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* PRICING SECTION */}
        {pricing?.plans?.length > 0 && (
          <section id="pricing" className="pricing-section bg-light">
            <div className="container">
              <h2 className="section-title text-center">{pricing.sectionTitle}</h2>
              <div className="pricing-toggle-wrapper">
                <span className={`toggle-label ${!isAnnualPricing ? 'active' : ''}`}>Monthly</span>
                <div className="toggle-switch" onClick={() => setIsAnnualPricing(!isAnnualPricing)}>
                  <div className={`toggle-knob ${isAnnualPricing ? 'annual' : 'monthly'}`}></div>
                </div>
                <span className={`toggle-label ${isAnnualPricing ? 'active' : ''}`}>Annually <span className="save-badge">2 Months Free</span></span>
              </div>

              <div className="pricing-grid">
                {pricing.plans.map((plan, i) => (
                  <div key={i} className={`pricing-card ${plan.popular ? 'popular' : ''}`}>
                    {plan.popular && <div className="popular-tag">MOST POPULAR</div>}
                    <div className="plan-name">{plan.name}</div>
                    <div className="plan-desc">{plan.desc}</div>
                    <div className="plan-price">
                      {plan.priceMonthly?.toLowerCase() === 'custom' ? (
                        <span className="amount">Custom</span>
                      ) : (
                        <>
                          <span className="currency">KSh</span>
                          <span className="amount">{isAnnualPricing ? plan.priceAnnually : plan.priceMonthly}</span>
                          <span className="period">/{isAnnualPricing ? 'year' : 'month'}</span>
                        </>
                      )}
                    </div>
                    {plan.priceMonthly?.toLowerCase() === 'custom' ? (
                      <a href="#contact" onClick={(e) => { e.preventDefault(); setIsDemoModalOpen(true); }} className={`btn-ghost outline w-100`}>{plan.cta}</a>
                    ) : (
                      <Link to="/register" className={`${plan.popular ? 'btn-primary' : 'btn-secondary'} w-100`}>{plan.cta}</Link>
                    )}
                    <ul className="plan-features">
                      {(plan.features || []).map((feature, idx) => (
                        <li key={idx}>{feature}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
              <p className="pricing-vat-notice text-center">Prices are subject to VAT where applicable.</p>
              <div className="pricing-help text-center mt-4">
                <p>Not sure which plan fits your business?</p>
                <a href={`https://wa.me/${config.support?.phone?.replace(/\+/g,'') || '254726642205'}?text=Hello%20${brand.productName},%20I%20need%20help%20choosing%20the%20right%20plan%20for%20my%20business.`} target="_blank" rel="noopener noreferrer" className="inline-whatsapp-link">
                  Talk to us on WhatsApp
                </a>
              </div>
            </div>
          </section>
        )}

        {/* COMPARISON TABLE */}
        <section className="comparison-section">
          <div className="container">
            <h3 className="text-center mb-4">Compare Plans in Detail</h3>
            <div className="table-responsive">
              <table className="comparison-table">
                <thead>
                  <tr>
                    <th>Features</th>
                    <th>Duka</th>
                    <th>Business</th>
                    <th>Growth</th>
                    <th>Enterprise</th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td>Point of Sale (POS)</td><td>✓</td><td>✓</td><td>✓</td><td>✓</td></tr>
                  <tr><td>Inventory Management</td><td>Basic</td><td>Advanced</td><td>Advanced</td><td>Custom</td></tr>
                  <tr><td>Customer Database</td><td>✓</td><td>✓</td><td>✓</td><td>✓</td></tr>
                  <tr><td>Credit / Debt Tracking</td><td>-</td><td>✓</td><td>✓</td><td>✓</td></tr>
                  <tr><td>Suppliers & Purchasing</td><td>-</td><td>✓</td><td>✓</td><td>✓</td></tr>
                  <tr><td>Expense Management</td><td>-</td><td>✓</td><td>✓</td><td>✓</td></tr>
                  <tr><td>Reports & Analytics</td><td>Basic</td><td>Advanced</td><td>Advanced</td><td>Custom</td></tr>
                  <tr><td>Multi-branch Support</td><td>-</td><td>-</td><td>✓</td><td>✓</td></tr>
                  <tr><td>Data Import/Export</td><td>✓</td><td>✓</td><td>✓</td><td>✓</td></tr>
                  <tr><td>Support Level</td><td>Email</td><td>Email & Chat</td><td>Priority</td><td>Dedicated Account Manager</td></tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* FAQ SECTION */}
        {faq?.items?.length > 0 && (
          <section id="faq" className="faq-section bg-light">
            <div className="container">
              <h2 className="section-title text-center">{faq.sectionTitle}</h2>
              <div className="faq-accordion">
                {faq.items.map((item, index) => (
                  <div className={`faq-item ${activeFaq === index ? 'active' : ''}`} key={index}>
                    <button className="faq-question" onClick={() => toggleFaq(index)} aria-expanded={activeFaq === index}>
                      {item.q}
                      <span className="faq-icon">{activeFaq === index ? '−' : '+'}</span>
                    </button>
                    <div className="faq-answer">
                      <p>{item.a}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* FINAL CTA */}
        <section className="final-cta">
          <div className="container text-center">
            <h2>{cta.headline}</h2>
            <p>{cta.subtext}</p>
            <div className="cta-buttons">
              <Link to={cta.primaryCtaLink} className="btn-primary btn-large">{cta.primaryCta}</Link>
              <button onClick={() => handleSecondaryCta(cta.secondaryCtaLink)} className="btn-secondary btn-large">{cta.secondaryCta}</button>
            </div>
            <p className="microcopy mt-2">{cta.microcopy}</p>
          </div>
        </section>

        {/* FOOTER */}
        <footer className="site-footer">
          <div className="container">
            <div className="footer-grid">
              <div className="footer-col brand-col">
                <Link to="/" className="brand-logo footer-logo">
                  {logoUrl ? (
                    <>
                      <img src={logoUrl} alt={brand.logoAlt} className="site-logo-img" style={{ height: '40px', objectFit: 'contain' }} />
                      <span className="brand-text">{brand.productName}</span>
                    </>
                  ) : (
                    <>
                      <span className="brand-icon">🛍️</span>
                      <span className="brand-text">{brand.productName}</span>
                    </>
                  )}
                </Link>
                <p className="footer-desc">{footer.about}</p>
                <div className="social-links">
                  {footer.social?.facebook && <a href={footer.social.facebook} aria-label="Facebook" target="_blank" rel="noopener noreferrer">FB</a>}
                  {footer.social?.instagram && <a href={footer.social.instagram} aria-label="Instagram" target="_blank" rel="noopener noreferrer">IG</a>}
                  {footer.social?.twitter && <a href={footer.social.twitter} aria-label="Twitter" target="_blank" rel="noopener noreferrer">X</a>}
                  {footer.social?.linkedin && <a href={footer.social.linkedin} aria-label="LinkedIn" target="_blank" rel="noopener noreferrer">IN</a>}
                </div>
              </div>
              
              <div className="footer-col">
                <h4>Product</h4>
                <a href="#features">Features</a>
                <a href="#pricing">Pricing</a>
                <a href="#industries">Industries</a>
                <button onClick={() => setIsDemoModalOpen(true)} className="text-link">Book Demo</button>
              </div>
              
              <div className="footer-col">
                <h4>Resources</h4>
                <a href="#faq">FAQ</a>
                <Link to="/contact">Help/Support</Link>
                <Link to="/contact">Contact</Link>
              </div>
              
              <div className="footer-col">
                <h4>Legal</h4>
                <Link to="/contact">Privacy Policy</Link>
                <Link to="/contact">Terms of Service</Link>
              </div>
            </div>
            
            <div className="footer-bottom">
              <p>{footer.copyright}</p>
            </div>
          </div>
        </footer>

        <WhatsAppButton />
        <DemoModal isOpen={isDemoModalOpen} onClose={() => setIsDemoModalOpen(false)} />
      </div>
    </>
  );
};

export default Landing;
